import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Clock, Moon, Heart, X, ChevronDown, ChevronUp } from 'lucide-react';

export function LifeClock({ language, birthDate, gender }) {
    const [enabled, setEnabled] = useState(() => localStorage.getItem('lifeclock_enabled') === 'true');
    const [now, setNow] = useState(new Date());
    const [showPhilosophy, setShowPhilosophy] = useState(false);
    const t = language === 'de';

    useEffect(() => {
        localStorage.setItem('lifeclock_enabled', enabled);
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, [enabled]);

    if (!birthDate) return null;

    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return null;

    // Life expectancy (Swiss average)
    const lifeExpectancy = gender === 'female' ? 85.2 : 82.3;
    const expectedEnd = new Date(birth);
    expectedEnd.setFullYear(expectedEnd.getFullYear() + Math.floor(lifeExpectancy));
    expectedEnd.setMonth(expectedEnd.getMonth() + Math.round((lifeExpectancy % 1) * 12));

    // Calculate remaining
    const totalMs = expectedEnd.getTime() - birth.getTime();
    const livedMs = now.getTime() - birth.getTime();
    const remainingMs = Math.max(expectedEnd.getTime() - now.getTime(), 0);

    const pctLived = Math.min((livedMs / totalMs) * 100, 100);

    const remainingYears = Math.floor(remainingMs / (365.25 * 24 * 60 * 60 * 1000));
    const remainingMonths = Math.floor((remainingMs % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
    const remainingDays = Math.floor((remainingMs % (30.44 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000));
    const remainingHours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const remainingMinutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
    const remainingSeconds = Math.floor((remainingMs % (60 * 1000)) / 1000);

    // Full moons remaining (synodic month = 29.53 days)
    const fullMoonsRemaining = Math.floor(remainingMs / (29.53 * 24 * 60 * 60 * 1000));

    // Lifestyle bonus estimate
    const bonusYears = 2.3;

    // Toggle section
    if (!enabled) {
        return (
            <Card className="border border-border">
                <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{t ? 'Lebens-Uhr' : 'Life Clock'}</span>
                    </div>
                    <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="lifeclock-toggle" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-2 border-primary/20 overflow-hidden" data-testid="life-clock">
            <CardContent className="p-0">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-sm">{t ? 'Dein Potenzial' : 'Your Potential'}</span>
                    </div>
                    <Switch checked={enabled} onCheckedChange={setEnabled} />
                </div>

                {/* Countdown */}
                <div className="p-4 text-center">
                    <div className="grid grid-cols-4 gap-1 mb-3">
                        <div>
                            <p className="text-2xl font-bold text-primary">{remainingYears}</p>
                            <p className="text-[9px] text-muted-foreground">{t ? 'Jahre' : 'Years'}</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{remainingMonths}</p>
                            <p className="text-[9px] text-muted-foreground">{t ? 'Monate' : 'Months'}</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{remainingDays}</p>
                            <p className="text-[9px] text-muted-foreground">{t ? 'Tage' : 'Days'}</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-muted-foreground">{String(remainingHours).padStart(2,'0')}:{String(remainingMinutes).padStart(2,'0')}:{String(remainingSeconds).padStart(2,'0')}</p>
                            <p className="text-[9px] text-muted-foreground">{t ? 'Std:Min:Sek' : 'H:M:S'}</p>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full transition-all" style={{ width: `${pctLived}%` }} />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        {pctLived.toFixed(1)}% {t ? 'erlebt' : 'lived'} — {(100 - pctLived).toFixed(1)}% {t ? 'vor dir' : 'ahead'}
                    </p>

                    {/* Full Moons */}
                    <div className="mt-3 flex items-center justify-center gap-2 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/20">
                        <Moon className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm">
                            <span className="font-bold text-indigo-700 dark:text-indigo-300">{fullMoonsRemaining}</span>
                            <span className="text-xs text-muted-foreground ml-1">{t ? 'Vollmonde vor dir' : 'full moons ahead'}</span>
                        </span>
                    </div>

                    {/* Philosophy — expandable */}
                    <button 
                        onClick={() => setShowPhilosophy(!showPhilosophy)}
                        className="mt-3 flex items-center justify-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors mx-auto"
                    >
                        {showPhilosophy ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {t ? 'Warum diese Uhr?' : 'Why this clock?'}
                    </button>
                    
                    {showPhilosophy && (
                        <div className="mt-2 p-4 rounded-xl bg-muted/50 text-left space-y-3">
                            <p className="text-sm leading-relaxed">
                                {t 
                                    ? 'Diese Uhr ist keine Mahnung. Sie ist eine Einladung.'
                                    : 'This clock is not a warning. It is an invitation.'
                                }
                            </p>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                                {t
                                    ? 'Wir verbringen so viel Lebenszeit damit, uns über Kleinigkeiten zu ärgern. Der Stau am Morgen. Die unhöfliche Nachricht. Der verpasste Zug. Und dabei vergessen wir, was wirklich zählt: der warme Kaffee in der Hand, das Lachen eines Freundes, der Moment, wenn die Sonne durchs Fenster fällt.'
                                    : 'We spend so much of our lives being upset about small things. Traffic jams. Rude messages. Missed trains. And we forget what truly matters: the warm coffee in our hands, a friend\'s laughter, the moment sunlight falls through the window.'
                                }
                            </p>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                                {t
                                    ? 'Jede Sekunde, die hier tickt, ist unwiederbringlich. Nicht um Angst zu machen — sondern um dich daran zu erinnern, dass dieses Leben endlich ist. Und genau das macht es so kostbar.'
                                    : 'Every second ticking here is irretrievable. Not to cause fear — but to remind you that this life is finite. And that\'s exactly what makes it so precious.'
                                }
                            </p>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                                {t
                                    ? 'Die Frage ist nicht, wie viel Zeit dir bleibt. Die Frage ist: Was machst du mit dem nächsten Moment? Ärgerst du dich über den Regen — oder tanzt du darin?'
                                    : 'The question isn\'t how much time you have left. The question is: What will you do with the next moment? Will you complain about the rain — or dance in it?'
                                }
                            </p>
                            <p className="text-sm font-medium text-center text-primary">
                                {t
                                    ? '🐺 Nanouk sagt: Weniger ärgern. Mehr staunen. Jeden Tag.'
                                    : '🐺 Nanouk says: Less worrying. More wondering. Every day.'
                                }
                            </p>
                        </div>
                    )}

                    {/* Motivational message — rotates weekly */}
                    {(() => {
                        const quotes_de = [
                            "Jeder Sonnenaufgang ist eine neue Chance. Nutze sie.",
                            "Das Glück liegt nicht am Ende des Weges — es liegt auf dem Weg.",
                            "Sammle Momente, keine Dinge.",
                            "Atme tief ein. Du bist genau da, wo du sein sollst.",
                            "Die beste Zeit im Leben ist immer jetzt.",
                            "Nicht die Jahre in deinem Leben zählen — sondern das Leben in deinen Jahren.",
                            "Heute ist ein guter Tag, jemandem zu sagen: Du bist wichtig.",
                            "Dankbarkeit verwandelt was wir haben in genug.",
                            "Ein Spaziergang im Wald heilt mehr als tausend Gedanken.",
                            "Weniger planen. Mehr erleben.",
                            "Die schönsten Erinnerungen entstehen ungeplant.",
                            "Du brauchst keinen perfekten Tag — nur einen echten.",
                        ];
                        const quotes_en = [
                            "Every sunrise is a new chance. Take it.",
                            "Happiness isn't at the end of the road — it's on the road.",
                            "Collect moments, not things.",
                            "Breathe deeply. You are exactly where you need to be.",
                            "The best time in life is always now.",
                            "It's not the years in your life — but the life in your years.",
                            "Today is a good day to tell someone: You matter.",
                            "Gratitude turns what we have into enough.",
                            "A walk in the forest heals more than a thousand thoughts.",
                            "Less planning. More living.",
                            "The most beautiful memories happen unplanned.",
                            "You don't need a perfect day — just a real one.",
                        ];
                        const quotes = t ? quotes_de : quotes_en;
                        // Change weekly based on week number
                        const weekNum = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
                        const quote = quotes[weekNum % quotes.length];
                        return <p className="mt-3 text-xs text-muted-foreground italic text-center">"{quote}"</p>;
                    })()}

                    {/* Lifestyle bonus */}
                    <div className="mt-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/20 text-xs text-green-700 dark:text-green-300">
                        {t 
                            ? `💚 Dein gesunder Lifestyle gibt dir statistisch +${bonusYears} Jahre`
                            : `💚 Your healthy lifestyle statistically gives you +${bonusYears} years`
                        }
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
